import {
  type PartBotResultJson,
  type PartFileViewerPageData,
  type PrintExtraction,
  PillDisplayType,
} from '@frontend/api';

import type { ActionTextSnippet } from 'modules/common/viewer/pdfViewer/annotationCommands';

import {
  controlFramePropsFromRawCallout,
  enclosureShapeFromRawCallout,
} from '../foundInFiles/controlFramePropsFromRawCallout';
import type {
  FindingData,
  FindingFileReference,
  FindingPopoverDisplayType,
} from './types';

export const toFindingData = (
  result: PartBotResultJson | PartBotResultJson[],
): FindingData => {
  const arr = Array.isArray(result) ? result : [result];
  const first = arr[0];
  const displayType: FindingPopoverDisplayType =
    first.displayType === PillDisplayType.pill ? 'pill' : 'text';

  const fileReferences: FindingFileReference[] = Array.isArray(result)
    ? (() => {
        const byFile = result.reduce<
          Record<string, { filename: string; count: number }>
        >((acc, r) => {
          if (r.partFileUuid in acc) {
            acc[r.partFileUuid].count += 1;
          } else {
            acc[r.partFileUuid] = { filename: r.filename, count: 1 };
          }
          return acc;
        }, {});

        // Convert the byFile object to an array of FindingFileReference objects
        return Object.entries(byFile).map(
          ([partFileUuid, { filename, count }]) => ({
            partFileUuid,
            filename,
            count,
          }),
        );
      })()
    : // If the result is not an array, return a single FindingFileReference object
      [
        {
          filename: first.filename,
          partFileUuid: first.partFileUuid,
          pageNumber: first.pageNumber,
        },
      ];

  return {
    label: first.value,
    description: first.description,
    displayType,
    count: arr.length,
    isGdt: !!first.rawCallout,
    fileReferences,
    controlFrameProps: controlFramePropsFromRawCallout(first.rawCallout),
    enclosureShape: enclosureShapeFromRawCallout(first.rawCallout),
    calloutType: first.rawCallout?.callout?.calloutType ?? null,
    regionType: first.rawRegion?.region?.regionType ?? null,
  };
};

export type FieldSuggestionFindingInput = {
  suggestion: string | object;
  sourceName: string;
  sourcePartFileId: string;
  id: number;
};

export const toFindingDataFromFieldSuggestion = (
  input: FieldSuggestionFindingInput,
  pageNumber?: number | null,
): FindingData => {
  const label = String(input.suggestion);
  return {
    label,
    description: label,
    displayType: 'text',
    count: 1,
    isGdt: false,
    fileReferences: [
      {
        filename: input.sourceName,
        partFileUuid: input.sourcePartFileId || `field-${input.id}`,
        pageNumber: pageNumber ?? null,
      },
    ],
  };
};

export type FeatureFeedbackFindingInput = {
  label: string;
  description: string;
  file: PartFileViewerPageData | undefined;
  onFileClick: () => void;
};

export const toFindingDataFromFeatureFeedback = (
  input: FeatureFeedbackFindingInput,
): FindingData => ({
  label: input.label,
  description: input.description,
  displayType: 'pill',
  count: 1,
  isGdt: false,
  fileReferences: [
    {
      filename: input.file?.filename ?? 'unknown file',
      partFileUuid: input.file?.uuid ?? '',
      onFileClick: input.onFileClick,
    },
  ],
});

export type TextSnippetFindingInput = {
  snippet: ActionTextSnippet;
  filename: string;
  onFileClick: () => void;
};

export const toFindingDataFromTextSnippet = (
  input: TextSnippetFindingInput,
): FindingData => ({
  label: input.snippet.text,
  description: input.snippet.text,
  displayType: 'text',
  count: 1,
  isGdt: false,
  fileReferences: [
    {
      filename: input.filename,
      partFileUuid: input.snippet.partFileUuid,
      pageNumber: input.snippet.pageNumber,
      onFileClick: input.onFileClick,
    },
  ],
});

export type PrintExtractionFindingInput = {
  extraction: PrintExtraction;
  filename: string;
  onFileClick: () => void;
};

export const toFindingDataFromPrintExtraction = (
  input: PrintExtractionFindingInput,
): FindingData => ({
  label: input.extraction.rawText,
  description: input.extraction.description,
  displayType: 'pill',
  count: 1,
  isGdt: !!input.extraction.rawCallout,
  fileReferences: [
    {
      filename: input.filename,
      partFileUuid: input.extraction.partFileUuid,
      pageNumber: input.extraction.pageNumber,
      onFileClick: input.onFileClick,
    },
  ],
  controlFrameProps: controlFramePropsFromRawCallout(
    input.extraction.rawCallout,
  ),
  enclosureShape: enclosureShapeFromRawCallout(input.extraction.rawCallout),
  calloutType: input.extraction.calloutType,
  regionType: input.extraction.regionType,
});
